import type { PermissionAction, UserRole, UserGroup } from '@/config/permissions'
import { useProfileStore } from '@/stores/profile'
const { mapPath } = usePath()

export type MenuItem = {
    label: string
    icon?: string
    to?: string
    children?: MenuItem[]
    requirePermissionsAny?: PermissionAction[]
    requireRolesAny?: UserRole[]
    requireGroupsAny?: UserGroup[]
    has_restriction_for_admin?: boolean,
    isVisible?: (ctx: {
        hasPermission: (a: PermissionAction) => boolean
        hasAnyRole: (r: UserRole[]) => boolean
        hasAnyGroup: (g: UserGroup[]) => boolean
        isSuperAdmin: boolean
    }) => boolean
}

// NOTE: Garder en phase avec les groupes produits par usePermissions.currentGroups
const ALL_GROUPS: UserGroup[] = [
    'Administration',
    'Pedagogie',
    'Secretariat',
    'Enseignement',
    'Etudiants',
    'Direction',
    'RH',
    'Maintenance'
]

function except(exclude: UserGroup[]): UserGroup[] {
    const set = new Set(exclude)
    return ALL_GROUPS.filter(g => !set.has(g))
}

// ============================================================================
// PROFILS DE VISIBILITÉ RÉUTILISABLES
// ============================================================================
// Ces constantes facilitent la maintenance en centralisant les règles de visibilité
// Utilisez ces constantes au lieu de répéter except([...]) partout
// IMPORTANT: La fonction except() reste disponible pour des cas spécifiques

/**
 * Visible par TOUS les groupes (y compris étudiants et personnel)
 * Groupes: Administration, Pedagogie, Secretariat, Enseignement, Etudiants, Direction
 */
export const VISIBILITY_ALL = ALL_GROUPS

/**
 * Visible par le personnel SAUF étudiants
 * Groupes: Administration, Pedagogie, Secretariat, Enseignement, Direction
 * Équivalent à: except(['Etudiants'])
 */
export const VISIBILITY_STAFF = except(['Etudiants'])

/**
 * Visible par le personnel SAUF étudiants et enseignants
 * Groupes: Administration, Pedagogie, Secretariat, Direction
 * Équivalent à: except(['Etudiants', 'Enseignement'])
 */
export const VISIBILITY_STAFF_NO_TEACHERS = except(['Etudiants', 'Enseignement'])

/**
 * Visible par administration, secrétariat et direction avec responsables pédagogiques
 * Groupes: Administration, Pedagogie, Secretariat, Direction
 * Équivalent à: except(['Etudiants', 'Enseignement'])
 * Alias de VISIBILITY_STAFF_NO_TEACHERS pour plus de clarté sémantique
 */
export const VISIBILITY_ADMIN_PEDAGOGIE = except(['Etudiants', 'Enseignement'])

/**
 * Visible UNIQUEMENT par administration, secrétariat et direction (sans pédagogie)
 * Groupes: Administration, Secretariat, Direction
 * Équivalent à: except(['Etudiants', 'Enseignement', 'Pedagogie'])
 */
export const VISIBILITY_ADMIN_ONLY = except(['Etudiants', 'Enseignement', 'Pedagogie'])

/**
 * Visible par la direction et administration uniquement
 * Groupes: Administration, Direction
 */
export const VISIBILITY_DIRECTION_ADMIN = ['Administration', 'Direction'] as UserGroup[]

/**
 * Visible par tous SAUF étudiants et responsables pédagogiques
 * Groupes: Administration, Secretariat, Enseignement, Direction
 * Équivalent à: except(['Etudiants', 'Pedagogie'])
 */
export const VISIBILITY_NO_STUDENTS_PEDAGOGIE = except(['Etudiants', 'Pedagogie'])

// ============================================================================
// DÉFINITION DU MENU
// ============================================================================


export const menuData: MenuItem[] = [
    // ---- GENERAL ---- (legacy: dashboard hiddenFor: [etudiant, personnel, responsable_pedagogique])
    {
        label: 'Tableau de bord',
        icon: 'ri-home-8-line',
        to: mapPath('/dashboard'),
        requireGroupsAny: except(['Etudiants', 'Enseignement', 'Maintenance' as UserGroup]),
    },

    // ---- ACADEMIE ---- (Scolarité)
    {
        label: 'Scolarité',
        icon: 'bx bx-id-card',
        // Le groupe Maintenance ne doit jamais voir ce bloc
        requireGroupsAny: except(['Etudiants', 'Enseignement', 'Maintenance' as UserGroup]),
        children: [
            // Enregistrement: Visible pour Admin, Pédagogie, Secrétariat, Direction (caché: Étudiants, Enseignement)
            { label: 'Enregistrement', to: mapPath('/academy/students/enrollment'), requireGroupsAny: except(['Etudiants', 'Enseignement', 'Maintenance' as UserGroup]) },
            // Paiements: Visible pour Admin, Secrétariat, Direction (caché: Étudiants, Enseignement, Pédagogie)
            // { label: 'Paiements', to: '#', requireGroupsAny: except(['Etudiants', 'Enseignement', 'Pedagogie']) },
            // Liste des étudiants: Visible pour tous sauf Enseignement (caché: Enseignement uniquement)
            { label: 'Liste des étudiants', to: mapPath('/academy/students'), requireGroupsAny: except(['Etudiants', 'Maintenance' as UserGroup]) },
            // Présences: Visible pour Admin, SuperAdmin, Responsables pédagogiques et Direction
            { label: 'Présences', icon: 'ri-user-voice-line', to: mapPath('/scolarite/presences'), requireGroupsAny: ['Administration', 'Pedagogie', 'Direction'] as UserGroup[] },
            { label: 'Ma fiche', to: mapPath('/academy/students/me'), requireGroupsAny: ['Etudiants'] as UserGroup[], has_restriction_for_admin: true },
        ]
    },

    {
        label: 'Planning',
        icon: 'bx bx-calendar',
        requireGroupsAny: except(['Etudiants', 'Maintenance' as UserGroup]),
        children: [
            // Calendriers pédagogiques: Visible pour tous (y compris étudiants)
            { label: 'Calendriers pédagogiques', to: mapPath('/planning/calendar'), requireGroupsAny: except(['Maintenance' as UserGroup]) },
            // Planning Enseignant: Visible pour Enseignement, Pédagogie, Admin, Secrétariat, Direction (caché: Étudiants)
            { label: 'Planning Enseignant', to: mapPath('/planning/enseignant'), requireGroupsAny: except(['Etudiants', 'Maintenance' as UserGroup]) },
            // Cahiers de texte: Visible pour tout le personnel (caché: Étudiants uniquement)
            { label: 'Cahiers de texte', to: mapPath('/planning/cahiers-texte'), requireGroupsAny: except(['Etudiants', 'Maintenance' as UserGroup]) },
            // Gestion plannings annuels: Visible pour Admin, Pédagogie, Secrétariat, Direction (caché: Étudiants, Enseignement)
            { label: 'Gestion des plannings annuels', to: mapPath('/planning'), requireGroupsAny: except(['Etudiants', 'Enseignement', 'Maintenance' as UserGroup]) },
            // Types d'activité: Visible pour Admin, Pédagogie, Secrétariat, Direction (caché: Étudiants, Enseignement)
            { label: "Types d'activité", to: mapPath('/planning/type-activites'), requireGroupsAny: except(['Etudiants', 'Enseignement', 'Maintenance' as UserGroup]) },
        ]
    },

    // Notes: Visible pour tous sauf Maintenance
    {
        label: 'Notes',
        icon: 'bx bx-edit',
        to: mapPath('/notes'),
        requireGroupsAny: except(['Maintenance' as UserGroup])
    },

    // Délibération: Menu principal dans ACADÉMIE, avec sous-menus selon les rôles
    {
        label: 'Délibération',
        icon: 'ri-file-list-3-line',
        requireGroupsAny: except(['Etudiants', 'Maintenance' as UserGroup]),
        children: [
            {
                label: 'Mes délibérations',
                to: mapPath('/deliberation'),
                requireGroupsAny: except(['Etudiants', 'Maintenance' as UserGroup])
            },
            {
                label: 'Mes prédélibérations',
                to: mapPath('/deliberation/predeliberation'),
                requireGroupsAny: ['Pedagogie', 'Administration'] as UserGroup[]
            },
            {
                label: 'Bulletins et attestations',
                to: mapPath('/deliberation/bulletins'),
                // Enseignement (enseignant) ne doit pas voir Bulletins
                // Prof responsable (Pedagogie) DOIT voir Bulletins
                requireGroupsAny: except(['Etudiants', 'Enseignement', 'Maintenance' as UserGroup])
            }
        ]
    },

    // Maquettes: Visible Admin, Pédagogie, Secrétariat, Direction (caché: Étudiants, Enseignement)
    {
        label: 'Maquettes',
        icon: 'bx bx-file',
        to: mapPath('/maquettes'),
        requireGroupsAny: except(['Etudiants', 'Enseignement', 'Maintenance' as UserGroup])
    },

    {
        label: 'Comptabilité',
        icon: 'ri-bill-line',
        requireGroupsAny: ['Administration', 'Pedagogie', 'Direction', 'Comptabilite'] as UserGroup[],
        children: [
            {
                label: 'Factures',
                to: mapPath('/comptabilite/factures'),
                requireGroupsAny: ['Administration', 'Pedagogie', 'Direction', 'Comptabilite'] as UserGroup[],
            },
            {
                label: 'Établir une facture',
                to: mapPath('/comptabilite/etablir'),
                requireGroupsAny: ['Administration', 'Pedagogie', 'Direction'] as UserGroup[],
            },
            {
                label: 'Modèles & tarifs',
                to: mapPath('/comptabilite/modeles'),
                requireGroupsAny: ['Administration'] as UserGroup[],
            },
        ],
    },

    // Fin d'année: Clôture + Préinscriptions (Admin / Pédagogie / Direction)
    {
        label: 'Fin d\'année',
        icon: 'ri-calendar-check-line',
        requireGroupsAny: ['Administration', 'Pedagogie', 'Direction'] as UserGroup[],
        children: [
            {
                label: 'Clôture des sessions',
                to: mapPath('/academy/cloture'),
                requireGroupsAny: ['Administration', 'Pedagogie', 'Direction'] as UserGroup[],
            },
            {
                label: 'Préinscriptions',
                to: mapPath('/academy/preinscriptions'),
                requireGroupsAny: ['Administration', 'Pedagogie', 'Direction'] as UserGroup[],
            },
        ],
    },

    // ---- ADMIN ----
    // Équipements & Locaux:
    // - Visible Admin, Secrétariat, Direction (caché: Étudiants, Enseignement, Pédagogie)
    // - ET c'est l'unique entrée visible pour le groupe Maintenance
    {
        label: 'Équipements & Locaux',
        icon: 'bx bx-building',
        to: mapPath('/infrastructure'),
        requireGroupsAny: [
            ...except(['Etudiants', 'Enseignement', 'Pedagogie']),
            'Maintenance' as UserGroup
        ]
    },

    // Personnel & RH: Visible pour RH et Administration (superadmin automatiquement)
    {
        label: 'Personnel & RH',
        icon: 'ri-team-line',
        requireGroupsAny: ['RH', 'Administration'] as UserGroup[],
        children: [
            // Personnel: Visible uniquement pour RH (et superadmin automatiquement)
            { label: 'Personnel', to: mapPath('/iam/staff'), requireGroupsAny: ['RH'] as UserGroup[] },
            // Présences du personnel: Visible uniquement pour Administration (et superadmin)
            { label: 'Présences du personnel', to: mapPath('/iam/presences-personnel'), requireGroupsAny: ['Administration'] as UserGroup[] },
            // Poste: Visible Admin, Secrétariat, Direction (caché: Étudiants, Enseignement, Pédagogie)
            { label: 'Poste', to: mapPath('/iam/postes'), requireGroupsAny: except(['Etudiants', 'Enseignement', 'Pedagogie', 'Maintenance' as UserGroup]) },
            // Affectation: Visible Admin, Secrétariat, Direction (caché: Étudiants, Enseignement, Pédagogie)
            { label: 'Affectation', to: mapPath('/iam/affectation'), requireGroupsAny: except(['Etudiants', 'Enseignement', 'Pedagogie', 'Maintenance' as UserGroup]) },
        ]
    },

    // Structure: Visible Admin, Secrétariat, Direction (caché: Étudiants, Enseignement, Pédagogie)
    {
        label: 'Structure',
        icon: 'bx bx-building-house',
        requireGroupsAny: except(['Etudiants', 'Enseignement', 'Pedagogie', 'Maintenance' as UserGroup]),
        children: [
            { label: 'Établissements', to: mapPath('/administration/etablissements'), requireGroupsAny: except(['Etudiants', 'Enseignement', 'Pedagogie', 'Maintenance' as UserGroup]) },
            { label: 'Directions', to: mapPath('/administration/directions'), requireGroupsAny: except(['Etudiants', 'Enseignement', 'Pedagogie', 'Maintenance' as UserGroup]) },
            { label: 'Services', to: mapPath('/administration/services'), requireGroupsAny: except(['Etudiants', 'Enseignement', 'Pedagogie', 'Maintenance' as UserGroup]) },
            { label: 'Départements', to: mapPath('/administration/departements'), requireGroupsAny: except(['Etudiants', 'Enseignement', 'Pedagogie', 'Maintenance' as UserGroup]) },
            { label: 'Formations', to: mapPath('/administration/formations'), requireGroupsAny: except(['Etudiants', 'Enseignement', 'Pedagogie', 'Maintenance' as UserGroup]) },
            { label: 'Parcours', to: mapPath('/administration/parcours'), requireGroupsAny: except(['Etudiants', 'Enseignement', 'Pedagogie', 'Maintenance' as UserGroup]) },
            { label: 'Filières', to: mapPath('/administration/filieres'), requireGroupsAny: except(['Etudiants', 'Enseignement', 'Pedagogie', 'Maintenance' as UserGroup]) },
            { label: 'Classes', to: mapPath('/administration/classes'), requireGroupsAny: except(['Etudiants', 'Enseignement', 'Pedagogie', 'Maintenance' as UserGroup]) },
        ]
    },

    // Rapports & Statistiques
    {
        label: 'Rapports & Statistiques',
        icon: 'ri-bar-chart-2-line',
        requireGroupsAny: except(['Etudiants', 'Enseignement', 'Maintenance' as UserGroup]),
        children: [
            {
                label: 'Statistiques étudiants',
                to: mapPath('/reports/statistiques'),
                requireGroupsAny: except(['Etudiants', 'Enseignement', 'Maintenance' as UserGroup]),
            },
            {
                label: 'Statistiques enseignement',
                to: mapPath('/reports/enseignement'),
                requireGroupsAny: ['Administration', 'Pedagogie'] as UserGroup[],
            },
        ],
    },

    // Paramètres: Visible Admin, Secrétariat, Direction (caché: Étudiants, Enseignement, Pédagogie)
    {
        label: 'Paramètres',
        icon: 'bx bx-cog',
        requireGroupsAny: except(['Etudiants', 'Enseignement', 'Pedagogie', 'Maintenance' as UserGroup]),
        children: [
            { label: 'Processus', to: mapPath('/parametre/processus'), requireGroupsAny: except(['Etudiants', 'Enseignement', 'Pedagogie', 'Maintenance' as UserGroup]) },
            { label: 'Utilisateurs & Groupe', to: mapPath('/parametre/utilisateur'), requireGroupsAny: except(['Etudiants', 'Enseignement', 'Pedagogie', 'Maintenance' as UserGroup]) },
            { label: 'Permission', to: '#', requireGroupsAny: except(['Etudiants', 'Enseignement', 'Pedagogie', 'Maintenance' as UserGroup]) },
            { label: 'Sessions', to: '#', requireGroupsAny: except(['Etudiants', 'Enseignement', 'Pedagogie', 'Maintenance' as UserGroup]) },
        ]
    },
]
